import { formatTime, formatTrafficFetchedAt } from "@/components/maps/mapUtils"
import i18n from "@/lib/i18n/i18n"
import type { ShownTrafficDelay } from "@/components/maps/routeTraffic"

const TRUCK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 lucide lucide-truck-icon lucide-truck"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>`
const CLOCK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 lucide lucide-clock-icon lucide-clock"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`
const ROUTE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 lucide lucide-route-icon lucide-route"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>`

const AMBER = "text-amber-600 dark:text-amber-400"

const row = (icon: string, value: string, label: string, valueClass = "") => `
  <div class="flex items-center gap-3">
    <div class="flex-shrink-0">${icon}</div>
    <div class="flex items-center gap-2">
      <span class="font-medium${valueClass ? ` ${valueClass}` : ""}">${value}</span>
      <span class="text-gray-500 dark:text-gray-400">${label}</span>
    </div>
  </div>`

const trafficLine = (
  driveSeconds: number,
  delaySeconds: number,
  scope: ShownTrafficDelay["scope"],
) => {
  const withoutSeconds = driveSeconds - delaySeconds
  const without =
    scope === "route" && withoutSeconds > 0
      ? ` <span class="whitespace-nowrap">· ${i18n.t("maps.routeInfoPopup.withoutTraffic", { time: formatTime(withoutSeconds) })}</span>`
      : ""
  return `
  <div class="pl-7 text-xs text-gray-500 dark:text-gray-400 whitespace-normal"><span class="whitespace-nowrap">${i18n.t("maps.routeInfoPopup.trafficIncluded", { time: `<span class="font-medium ${AMBER}">${formatTime(delaySeconds)}</span>` })}</span>${without}</div>`
}

// gps refreshes traffic every 30 min while the truck moves. Past 15 min a refresh is
// due soon; past 45 min one was missed, usually because the truck is parked.
const FRESH_TRAFFIC_MINUTES = 15
const OUTDATED_TRAFFIC_MINUTES = 45

const FRESHNESS = {
  fresh: {
    dot: "bg-green-500",
    label: "maps.routeInfoPopup.freshness.fresh",
  },
  aging: {
    dot: "bg-amber-500",
    label: "maps.routeInfoPopup.freshness.aging",
  },
  outdated: {
    dot: "bg-gray-400",
    label: "maps.routeInfoPopup.freshness.outdated",
  },
} as const

const trafficFooter = (
  trafficFetchedAt: string | null | undefined,
  delayed: boolean,
  now: number,
) => {
  const fetchedAt = trafficFetchedAt ? Date.parse(trafficFetchedAt) : NaN
  if (Number.isNaN(fetchedAt)) return ""
  const clock = formatTrafficFetchedAt(fetchedAt, now)
  const minutes = Math.max(0, Math.floor((now - fetchedAt) / 60_000))
  const freshness =
    minutes < FRESH_TRAFFIC_MINUTES
      ? "fresh"
      : minutes <= OUTDATED_TRAFFIC_MINUTES
        ? "aging"
        : "outdated"
  const age =
    minutes === 0
      ? i18n.t("maps.routeInfoPopup.justNow")
      : i18n.t("maps.routeInfoPopup.minutesAgo", { value: minutes })
  const text =
    freshness === "outdated"
      ? i18n.t("maps.routeInfoPopup.outdatedFooter", { clock })
      : i18n.t(
          delayed
            ? "maps.routeInfoPopup.checkedDelayed"
            : "maps.routeInfoPopup.checkedNoDelay",
          { age, clock },
        )
  const { dot } = FRESHNESS[freshness]
  const label = i18n.t(FRESHNESS[freshness].label)
  return `
  <div class="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400" data-freshness="${freshness}">
    <span class="inline-block w-2 h-2 rounded-full ${dot}" role="img" aria-label="${label}" title="${label}"></span>
    <span>${text}</span>
  </div>`
}

const popupFrame = (
  durationRows: string,
  distanceMeters: number,
  footer = "",
) =>
  `<div class="space-y-2">
    <div class="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg pr-10 whitespace-nowrap">
      <div class="flex items-center gap-3">
        <div class="flex-grow">
          ${durationRows}
          ${row(ROUTE_ICON, `${(distanceMeters / 1000).toFixed(0)} km`, i18n.t("maps.mapManager.distance"))}${footer}
        </div>
      </div>
    </div>
  </div>`

export const routeInfoPopupHtml = ({
  driveSeconds,
  withBreaksSeconds,
  trafficDelay,
  distanceMeters,
  trafficFetchedAt,
  now = Date.now(),
}: {
  driveSeconds: number
  withBreaksSeconds?: number | null
  trafficDelay: ShownTrafficDelay
  distanceMeters: number
  trafficFetchedAt?: string | null
  now?: number
}) => {
  const { seconds: delaySeconds, scope } = trafficDelay
  const delayed = delaySeconds !== null
  const driving =
    row(
      CLOCK_ICON,
      formatTime(driveSeconds),
      i18n.t("maps.mapManager.driving"),
      delayed ? AMBER : "",
    ) + (delayed ? trafficLine(driveSeconds, delaySeconds, scope) : "")
  const withBreaks =
    withBreaksSeconds == null
      ? ""
      : row(
          TRUCK_ICON,
          formatTime(withBreaksSeconds),
          i18n.t("maps.mapManager.withBreaks"),
        )
  return popupFrame(
    driving + withBreaks,
    distanceMeters,
    trafficFooter(trafficFetchedAt, delayed, now),
  )
}

export const analyzeOrderRoutePopupHtml = ({
  driveSeconds,
  distanceMeters,
}: {
  driveSeconds: number
  distanceMeters: number
}) =>
  popupFrame(
    row(
      TRUCK_ICON,
      formatTime(driveSeconds),
      i18n.t("maps.mapManager.driving"),
    ),
    distanceMeters,
  )

/**
 * Redraws an open popup every minute, so "N min ago" keeps counting. Returns
 * the redraw for other triggers; it does nothing once the popup closes.
 */
export const redrawEveryMinute = (
  popup: {
    setHTML: (html: string) => unknown
    once: (type: "close", listener: () => void) => unknown
  },
  html: () => string,
  afterRedraw: () => void,
) => {
  let closed = false
  const redraw = () => {
    if (closed) return
    popup.setHTML(html())
    afterRedraw()
  }
  const timer = setInterval(redraw, 60_000)
  popup.once("close", () => {
    closed = true
    clearInterval(timer)
  })
  return redraw
}
